import { BankOutlineIcon, CopyIcon, MMButton } from '@components'
import DownloadIcon from '@icons/download.svg?react'
import cn from '@integrations/classnames'
import { useEffect, useRef, useState } from '@integrations/react'
import { Banner } from '@onboarding'
import {
  formatIbanAddress,
  IbanRequestStatuses,
  useCurrencies,
  useDownloadProofOfAccountOwnership
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import {
  copyTextToClipboard,
  formatIban,
  getCountryName,
  humanizedEnumValue,
  isValidArray,
  tr,
  translateCountryName
} from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import styles from './AccountPaymentDetails.module.scss'

interface DetailTile {
  key: string
  label: string
  /** What the copy button puts on the clipboard. */
  value?: string
  /** Shown instead of `value` when the readable form differs (e.g. IBAN grouped in blocks of 4). */
  display?: string
  /** Shown when there is no value yet (e.g. "Not issued"). */
  placeholder?: string
  copy?: boolean
  wrap?: boolean
}

// Not part of `IbanRequestStatuses`, but it is what the backend reports once the request is approved
const IBAN_APPROVED = 'APPROVED'

/** Full country name for an alpha-3 code; an unknown code is shown as received. */
function countryName(code?: string) {
  if (!code) return ''
  return getCountryName(code) ? translateCountryName(code) : code
}

interface Props {
  account: AccountBalanceResponse
  /** Set when the account can still get a named IBAN. */
  onRequestIban: (() => void) | null
  /** The account's currency can have an IBAN issued; otherwise the IBAN tile is hidden (unless it already has one). */
  ibanSupported: boolean
}

/** The account's payment detail tiles, its proof of ownership and the request-IBAN banner. */
function AccountPaymentDetails({ account, onRequestIban, ibanSupported }: Props) {
  const { id, currencyCode, type, dedicatedMerchantBankAccounts, ibanStatus, cryptoAddress } = account
  const bankAccount = isValidArray(dedicatedMerchantBankAccounts) ? dedicatedMerchantBankAccounts[0] : null

  const { isCryptoCurrency } = useCurrencies()
  const isCrypto = isCryptoCurrency(currencyCode)
  const ibanRequested = (
    [IbanRequestStatuses.CREATED, IbanRequestStatuses.PENDING, IbanRequestStatuses.WAITING_ACTION] as string[]
  ).includes(ibanStatus)
  const ibanApproved = ibanStatus === IBAN_APPROVED
  // Only an approved request that already carries the IBAN counts; a declined or any other status
  // looks exactly like an account without an IBAN, whatever bank data is still attached
  const ibanIssued = ibanApproved && Boolean(bankAccount?.iban)
  const { downloadFile, downloadFileError, downloadFileFetching } = useDownloadProofOfAccountOwnership()

  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(copiedTimer.current), [])

  function handleCopy(field: string, value?: string) {
    if (!value) return
    copyTextToClipboard(value)
    setCopiedField(field)
    // a newer copy restarts the mark instead of being cleared by the previous timer
    clearTimeout(copiedTimer.current)
    copiedTimer.current = setTimeout(() => setCopiedField(null), 2000)
  }

  const idTile: DetailTile = {
    key: 'id',
    label: tr('FRONTEND.ACCOUNTS.INFO.ID', 'Account ID'),
    value: id ? String(id) : '',
    copy: true
  }

  function issuedIbanTiles(): DetailTile[] {
    const { iban, bicSwift, holderName, bankName, accountNumber, bankCode, branchCode, country } = bankAccount
    const { bankAddress, holderAddress, ownerAddress } = bankAccount
    const beneficiaryAddress =
      ownerAddress ||
      formatIbanAddress(
        holderAddress && {
          ...holderAddress,
          countryName: holderAddress.countryName || countryName(holderAddress.countryCode)
        }
      )

    // The order a payer fills in a transfer: who is paid, which account, at which bank, then the portal's own
    // references. Internal flags (personal, settlement, prepaid card, merchant ID) are never listed
    const tiles: DetailTile[] = [
      {
        key: 'beneficiary',
        label: tr('FRONTEND.ACCOUNTS.INFO.BENEFICIARY', 'Beneficiary'),
        value: holderName,
        copy: true
      },
      {
        key: 'beneficiary-address',
        label: tr('FRONTEND.ACCOUNTS.INFO.BENEFICIARY_ADDRESS', 'Beneficiary address'),
        value: beneficiaryAddress,
        wrap: true
      },
      { key: 'iban', label: 'IBAN', value: iban, display: formatIban(iban), copy: true, wrap: true },
      { key: 'bic', label: tr('FRONTEND.ACCOUNTS.INFO.BIC_SWIFT', 'BIC/SWIFT'), value: bicSwift, copy: true },
      {
        key: 'account-number',
        label: tr('FRONTEND.ACCOUNTS.INFO.ACCOUNT_NUMBER', 'Account number'),
        value: accountNumber,
        copy: true,
        wrap: true
      },
      { key: 'bank-code', label: tr('FRONTEND.ACCOUNTS.INFO.BANK_CODE', 'Bank code'), value: bankCode },
      { key: 'branch-code', label: tr('FRONTEND.ACCOUNTS.INFO.BRANCH_CODE', 'Branch code'), value: branchCode },
      { key: 'bank-name', label: tr('FRONTEND.ACCOUNTS.INFO.BANK_NAME', 'Bank name'), value: bankName, wrap: true },
      {
        key: 'bank-country',
        label: tr('FRONTEND.ACCOUNTS.INFO.BANK_COUNTRY', 'Bank country'),
        value: countryName(country) || countryName(bankAddress?.countryCode)
      },
      idTile,
      {
        key: 'account-type',
        label: tr('FRONTEND.ACCOUNTS.INFO.ACCOUNT_TYPE', 'Account type'),
        value: humanizedEnumValue(type)
      }
    ]
    return tiles.filter(({ value }) => Boolean(value?.trim()))
  }

  function ibanTiles(): DetailTile[] {
    if (ibanIssued) return issuedIbanTiles()
    // Without an issued IBAN there is no beneficiary yet — only the IBAN status for eligible accounts
    return [
      idTile,
      ...(ibanRequested || ibanSupported
        ? [
            {
              key: 'iban',
              label: 'IBAN',
              placeholder: ibanRequested
                ? tr('FRONTEND.ACCOUNTS.IBAN_REQUESTED.TITLE', 'IBAN requested')
                : tr('FRONTEND.ACCOUNTS.INFO.NOT_ISSUED', 'Not issued'),
              copy: true
            }
          ]
        : [])
    ]
  }

  // Beneficiary and IBAN are fiat-banking concepts — never shown for crypto accounts (e.g. BTC)
  const details: DetailTile[] = isCrypto
    ? [
        idTile,
        ...(cryptoAddress
          ? [
              {
                key: 'crypto-address',
                label: tr('FRONTEND.ACCOUNTS.DETAILS.CRYPTO_ADDRESS', 'Crypto address'),
                value: cryptoAddress,
                copy: true,
                // an address must stay readable end to end, so it wraps instead of truncating
                wrap: true
              }
            ]
          : [])
      ]
    : ibanTiles()

  return (
    <>
      <div className={cn(styles['account-details__grid'], 'account-details__grid')}>
        {details.map(({ key, label, value, display, placeholder, copy, wrap }) => (
          <div key={key} className={cn(styles['detail-tile'], 'detail-tile')}>
            <div className={cn(styles['detail-tile__text'], 'detail-tile__text')}>
              <div className={cn(styles['detail-tile__label'], 'detail-tile__label')}>{label}</div>
              <div
                className={cn(styles['detail-tile__value'], 'detail-tile__value', {
                  [styles['empty']]: !value,
                  empty: !value,
                  [styles['detail-tile__value--wrap']]: wrap,
                  'detail-tile__value--wrap': wrap
                })}
                data-test={`account-${key}`}
              >
                {display || value || placeholder || '—'}
              </div>
            </div>
            {copy && (
              <button
                type='button'
                className={cn(styles['detail-tile__copy'], 'detail-tile__copy')}
                onClick={() => handleCopy(key, value)}
                disabled={!value}
                aria-label={tr('FRONTEND.ACCOUNTS.INFO.COPY', 'Copy {{FIELD}}', { FIELD: label })}
                data-test={`copy-${key}`}
              >
                {copiedField === key ? (
                  <span className={cn(styles['detail-tile__copied'], 'detail-tile__copied')}>
                    {tr('FRONTEND.ACCOUNTS.INFO.COPIED', 'Copied!')}
                  </span>
                ) : (
                  <CopyIcon color='currentColor' variant='compact' />
                )}
              </button>
            )}
          </div>
        ))}
      </div>

      <ErrorDisplay error={downloadFileError} />

      {ibanIssued && (
        <div>
          <MMButton
            type='button'
            variant='secondary'
            leftIcon={DownloadIcon}
            caption={tr('FRONTEND.ACCOUNTS.INFO.PROOF_OF_ACCOUNT', 'Proof of account')}
            onClick={() => downloadFile(id)}
            loading={downloadFileFetching}
            data-test='proof-of-account'
          />
        </div>
      )}

      {onRequestIban && !ibanApproved && !ibanRequested && (
        <Banner
          tone='action'
          icon={<BankOutlineIcon />}
          title={tr('FRONTEND.ACCOUNTS.WORKSPACE.IBAN_TITLE_CURRENCY', 'Get your own named {{CURRENCY}} IBAN', {
            CURRENCY: currencyCode
          })}
          content={
            currencyCode === 'EUR'
              ? tr(
                  'FRONTEND.DASHBOARD.ISSUE_IBAN_BANNER.SUBTITLE',
                  'Send and receive SEPA payments with your own dedicated IBAN.'
                )
              : tr(
                  'FRONTEND.ACCOUNTS.WORKSPACE.IBAN_SUBTITLE_GENERIC',
                  'Send and receive payments with your own dedicated IBAN.'
                )
          }
          handleClick={onRequestIban}
          actionLabel={tr('FRONTEND.ACCOUNTS.REQUEST_IBAN_BUTTON', 'Request IBAN')}
          actionTestId='button-iban-request-account'
          dataTest='account-iban-banner'
        />
      )}
    </>
  )
}

export default AccountPaymentDetails
